import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  ClipboardCheck, Camera, FileText, Type, PenLine, Upload, Sparkles,
  CheckCircle2, XCircle, Lock, ArrowLeft, Loader2, Send, Trophy,
  MessageSquareText, Trash2, GraduationCap,
} from 'lucide-react';
import SubjectThumbnail from '@/components/SubjectThumbnail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
  { id: 'maths', name: 'Maths' },
  { id: 'english-language', name: 'English Language' },
  { id: 'english-literature', name: 'English Literature' },
  { id: 'biology', name: 'Biology' },
  { id: 'chemistry', name: 'Chemistry' },
  { id: 'physics', name: 'Physics' },
  { id: 'geography', name: 'Geography' },
  { id: 'history', name: 'History' },
  { id: 'economics', name: 'Economics' },
];
const BOARDS = ['AQA', 'Edexcel', 'OCR', 'WJEC/Eduqas', 'CIE', 'Other / Not sure'];
const SKILL_LABELS = {
  knowledge: 'Knowledge', exam_technique: 'Exam Technique', time_management: 'Time Management',
  evaluation: 'Evaluation Skills', accuracy: 'Working Accuracy', vocabulary: 'Vocabulary',
};

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function MarkMyWork() {
  const navigate = useNavigate();
  const [access, setAccess] = useState(null); // null=loading
  const [subject, setSubject] = useState('maths');
  const [board, setBoard] = useState('');
  const [qNumber, setQNumber] = useState('');
  const [marks, setMarks] = useState('');
  const [markScheme, setMarkScheme] = useState('');
  const [showScheme, setShowScheme] = useState(false);
  const [inputMode, setInputMode] = useState('photo');
  const [files, setFiles] = useState([]);
  const [typedText, setTypedText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/mark-my-work/access`, { headers: authHeader() })
      .then((r) => setAccess(r.data))
      .catch(() => setAccess({ has_access: false, reason: 'auth', message: 'Please sign in.' }));
    loadHistory();
  }, []);

  const loadHistory = () => {
    axios.get(`${API}/mark-my-work/submissions`, { headers: authHeader() })
      .then((r) => setHistory(r.data.submissions || [])).catch(() => {});
  };

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked].slice(0, 6));
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    const hasText = (inputMode === 'typed' || inputMode === 'editor') && typedText.trim().length > 10;
    const hasFiles = (inputMode === 'photo' || inputMode === 'pdf') && files.length > 0;
    if (!hasText && !hasFiles) {
      toast.error('Add your answer as text, a photo or a PDF first.');
      return;
    }
    const fd = new FormData();
    fd.append('subject', subject);
    fd.append('question_number', qNumber);
    fd.append('exam_board', board);
    fd.append('marks_available', marks || '0');
    fd.append('input_type', inputMode);
    fd.append('mark_scheme', markScheme);
    fd.append('authorization', `Bearer ${localStorage.getItem('token')}`);
    if (hasText) fd.append('typed_text', typedText);
    if (hasFiles) files.forEach((f) => fd.append('files', f));

    setSubmitting(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/mark-my-work/submit`, fd);
      setResult(r.data.result);
      setSubmissionId(r.data.submission_id);
      loadHistory();
      toast.success('Marked! Scroll down for your feedback.');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === 'object' ? d?.message : d) || 'Marking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openSubmission = async (id) => {
    try {
      const r = await axios.get(`${API}/mark-my-work/submission/${id}`, { headers: authHeader() });
      setResult(r.data.result);
      setSubmissionId(id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch { toast.error('Could not load that submission.'); }
  };

  // ---- Gating screens -------------------------------------------------------
  if (access === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!access.has_access) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-slate-200 shadow-sm" data-testid="mmw-locked">
          <CardContent className="p-8 text-center">
            <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Lock className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Mark My Work</h1>
            <p className="text-slate-600 mb-6">{access.message}</p>
            {access.reason === 'subscription_required' ? (
              <Button onClick={() => navigate('/subscription')} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="mmw-upgrade-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Upgrade to Premium
              </Button>
            ) : (
              <Button onClick={() => navigate('/welcome')} variant="outline" className="w-full" data-testid="mmw-back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to dashboard
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Main -----------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white" data-testid="mmw-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate('/welcome')} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-4" data-testid="mmw-back-link">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </button>

        <div className="flex items-center gap-4 mb-2">
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0">
            <ClipboardCheck className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              Mark My Work <Badge className="bg-emerald-100 text-emerald-700 border-0">Examinor™</Badge>
            </h1>
            <p className="text-slate-600 text-sm">Upload your work and get instant AI-estimated marking, grade & feedback.</p>
          </div>
        </div>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block mb-6">
          ⚠️ Marks are <b>AI-estimated</b> for practice — not official exam-board marks.
        </p>

        {access.reason === 'trial' && (
          <div className="flex items-center justify-between gap-3 flex-wrap bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 mb-6" data-testid="mmw-trial-banner">
            <p className="text-sm text-emerald-800 flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              You're on your <b>free 5-day trial</b> — {access.trial_days_left} day{access.trial_days_left === 1 ? '' : 's'} left.
            </p>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/subscription')} data-testid="mmw-trial-upgrade">
              Upgrade to keep it
            </Button>
          </div>
        )}

        {/* Input card */}
        <Card className="border-slate-200 shadow-sm mb-6" data-testid="mmw-input-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-slate-900">1. Tell us about the work</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Subject</label>
                <Select value={subject} onValueChange={setSubject}>
                  <SelectTrigger data-testid="mmw-subject-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SUBJECTS.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Exam board (optional)</label>
                <Select value={board} onValueChange={setBoard}>
                  <SelectTrigger data-testid="mmw-board-select"><SelectValue placeholder="Select board" /></SelectTrigger>
                  <SelectContent>
                    {BOARDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Question number (optional)</label>
                <Input value={qNumber} onChange={(e) => setQNumber(e.target.value)} placeholder="e.g. Q3" data-testid="mmw-qnum-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Marks available (optional)</label>
                <Input value={marks} onChange={(e) => setMarks(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 10" data-testid="mmw-marks-input" />
              </div>
            </div>
            <button className="text-xs text-emerald-700 hover:underline" onClick={() => setShowScheme((s) => !s)} data-testid="mmw-toggle-scheme">
              {showScheme ? '– Hide mark scheme' : '+ Paste a mark scheme for precise marking (optional)'}
            </button>
            {showScheme && (
              <Textarea value={markScheme} onChange={(e) => setMarkScheme(e.target.value)} rows={4}
                placeholder="Paste the official mark scheme here for stricter marking…" data-testid="mmw-scheme-input" />
            )}
          </CardContent>
        </Card>

        {/* Upload card */}
        <Card className="border-slate-200 shadow-sm mb-6" data-testid="mmw-upload-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-slate-900">2. Add your answer</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={inputMode} onValueChange={setInputMode}>
              <TabsList className="grid grid-cols-4 mb-4">
                <TabsTrigger value="photo" data-testid="mmw-tab-photo"><Camera className="h-4 w-4 mr-1" />Photo</TabsTrigger>
                <TabsTrigger value="pdf" data-testid="mmw-tab-pdf"><FileText className="h-4 w-4 mr-1" />PDF</TabsTrigger>
                <TabsTrigger value="typed" data-testid="mmw-tab-typed"><Type className="h-4 w-4 mr-1" />Typed</TabsTrigger>
                <TabsTrigger value="editor" data-testid="mmw-tab-editor"><PenLine className="h-4 w-4 mr-1" />Editor</TabsTrigger>
              </TabsList>

              <TabsContent value="photo">
                <UploadZone accept="image/*" label="Take or upload photo(s) of your work" files={files} fileRef={fileRef} onFiles={onFiles} removeFile={removeFile} testid="mmw-photo-drop" />
              </TabsContent>
              <TabsContent value="pdf">
                <UploadZone accept="application/pdf" label="Upload a PDF of your answer" files={files} fileRef={fileRef} onFiles={onFiles} removeFile={removeFile} testid="mmw-pdf-drop" />
              </TabsContent>
              <TabsContent value="typed">
                <Textarea value={typedText} onChange={(e) => setTypedText(e.target.value)} rows={8}
                  placeholder="Paste your typed answer here…" data-testid="mmw-typed-input" />
              </TabsContent>
              <TabsContent value="editor">
                <Textarea value={typedText} onChange={(e) => setTypedText(e.target.value)} rows={12}
                  placeholder="Write your answer directly here…" className="font-serif" data-testid="mmw-editor-input" />
              </TabsContent>
            </Tabs>

            <Button onClick={submit} disabled={submitting} className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 h-11" data-testid="mmw-submit-btn">
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Marking your work…</> : <><ClipboardCheck className="h-4 w-4 mr-2" />Mark My Work</>}
            </Button>
          </CardContent>
        </Card>

        {result && <ResultDashboard result={result} submissionId={submissionId} />}

        {history.length > 0 && (
          <Card className="border-slate-200 shadow-sm mt-6" data-testid="mmw-history">
            <CardHeader className="pb-2"><CardTitle className="text-base text-slate-900">Recent submissions</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {history.map((h) => (
                <button key={h.id} onClick={() => openSubmission(h.id)}
                  className="w-full flex items-center justify-between text-left px-3 py-2 rounded-lg hover:bg-slate-50 border border-slate-100"
                  data-testid={`mmw-history-item-${h.id}`}>
                  <span className="flex items-center gap-2 text-sm text-slate-700">
                    <SubjectThumbnail subject={h.subject} size="sm" animate={false} />
                    {h.subject_name} {h.question_number && `· ${h.question_number}`}
                  </span>
                  <Badge variant="outline">{(h.result?.score ?? '–')}/{h.result?.marks_available ?? h.marks_available ?? '?'}</Badge>
                </button>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- upload zone
const UploadZone = ({ accept, label, files, fileRef, onFiles, removeFile, testid }) => (
  <div>
    <div onClick={() => fileRef.current?.click()}
      className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors"
      data-testid={testid}>
      <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
      <p className="text-sm text-slate-600">{label}</p>
      <p className="text-xs text-slate-400 mt-1">Up to 6 files · max 15MB each</p>
      <input ref={fileRef} type="file" accept={accept} multiple hidden onChange={onFiles} data-testid="mmw-file-input" />
    </div>
    {files.length > 0 && (
      <div className="flex flex-wrap gap-2 mt-3">
        {files.map((f, i) => (
          <div key={i} className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-1.5 text-xs text-slate-700">
            <FileText className="h-3.5 w-3.5" /> <span className="max-w-[160px] truncate">{f.name}</span>
            <button onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    )}
  </div>
);

// --------------------------------------------------------------------------- result dashboard
const ResultDashboard = ({ result, submissionId }) => {
  const [showChat, setShowChat] = useState(false);
  const pct = result.marks_available ? Math.round((result.score / result.marks_available) * 100) : null;
  const skills = result.skills || {};
  return (
    <div className="space-y-6" data-testid="mmw-result">
      {/* Score header */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 text-white">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-emerald-50 text-sm">{result.subject} {result.question_number && `· ${result.question_number}`} {result.exam_board && `· ${result.exam_board}`}</p>
              <div className="text-4xl font-bold mt-1" data-testid="mmw-score">
                {result.score}<span className="text-emerald-100 text-2xl">/{result.marks_available}</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <Stat label="Predicted Grade" value={result.predicted_grade || '–'} />
              <Stat label="Confidence" value={result.confidence != null ? `${result.confidence}%` : '–'} />
              <Stat label="Est. Level" value={result.estimated_level || '–'} small />
            </div>
          </div>
          {pct != null && <Progress value={pct} className="mt-4 h-2 bg-emerald-700/40" />}
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-slate-200 shadow-sm" data-testid="mmw-awarded">
          <CardHeader className="pb-2"><CardTitle className="text-base text-emerald-700 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />Marks Awarded</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(result.marks_awarded || []).map((m, i) => (
              <p key={i} className="text-sm text-slate-700 flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />{m}</p>
            ))}
            {!(result.marks_awarded || []).length && <p className="text-sm text-slate-400">–</p>}
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm" data-testid="mmw-lost">
          <CardHeader className="pb-2"><CardTitle className="text-base text-rose-600 flex items-center gap-2"><XCircle className="h-4 w-4" />Marks Lost</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(result.marks_lost || []).map((m, i) => (
              <p key={i} className="text-sm text-slate-700 flex gap-2"><XCircle className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" />{m}</p>
            ))}
            {!(result.marks_lost || []).length && <p className="text-sm text-slate-400">Full marks — brilliant!</p>}
          </CardContent>
        </Card>
      </div>

      {result.examiner_comments && (
        <Card className="border-slate-200 shadow-sm bg-indigo-50/40" data-testid="mmw-comments">
          <CardHeader className="pb-2"><CardTitle className="text-base text-slate-900 flex items-center gap-2"><GraduationCap className="h-4 w-4 text-indigo-600" />AI Examiner Comments</CardTitle></CardHeader>
          <CardContent><p className="text-sm text-slate-700 italic">“{result.examiner_comments}”</p></CardContent>
        </Card>
      )}

      {(result.ao_breakdown || []).length > 0 && (
        <Card className="border-slate-200 shadow-sm" data-testid="mmw-ao">
          <CardHeader className="pb-2"><CardTitle className="text-base text-slate-900">Assessment Objectives</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {result.ao_breakdown.map((ao, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-slate-800">{ao.ao} {ao.descriptor && <span className="text-slate-400 font-normal">— {ao.descriptor}</span>}</span>
                  <span className="text-slate-600">{ao.score}/{ao.max}</span>
                </div>
                {ao.max ? <Progress value={Math.round((ao.score / ao.max) * 100)} className="h-1.5" /> : null}
                {ao.comment && <p className="text-xs text-slate-500 mt-1">{ao.comment}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Learning coach */}
      {result.learning_coach && (
        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-violet-50 to-fuchsia-50" data-testid="mmw-coach">
          <CardHeader className="pb-2"><CardTitle className="text-base text-slate-900 flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" />AI Learning Coach</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-3 gap-4 text-sm">
            <CoachCol title="You've mastered" items={result.learning_coach.mastered} color="text-emerald-700" />
            <CoachCol title="Spend 20 mins on" items={result.learning_coach.improve} color="text-amber-700" />
            <CoachCol title="Recommended next steps" items={result.learning_coach.next_steps} color="text-violet-700" />
          </CardContent>
        </Card>
      )}

      {/* Skills */}
      {Object.keys(skills).length > 0 && (
        <Card className="border-slate-200 shadow-sm" data-testid="mmw-skills">
          <CardHeader className="pb-2"><CardTitle className="text-base text-slate-900 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" />Exam Skills Profile</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {Object.entries(SKILL_LABELS).map(([k, label]) => (
              skills[k] != null && (
                <div key={k}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-slate-700">{label}</span><span className="text-slate-500">{skills[k]}%</span></div>
                  <Progress value={skills[k]} className="h-2" />
                </div>
              )
            ))}
          </CardContent>
        </Card>
      )}

      <Button variant="outline" className="w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50" onClick={() => setShowChat((s) => !s)} data-testid="mmw-explain-btn">
        <MessageSquareText className="h-4 w-4 mr-2" /> Explain My Marks
      </Button>
      {showChat && <ExplainChat submissionId={submissionId} />}
    </div>
  );
};

const Stat = ({ label, value, small }) => (
  <div>
    <div className={`font-bold ${small ? 'text-sm' : 'text-lg'}`}>{value}</div>
    <div className="text-[11px] text-emerald-50/90">{label}</div>
  </div>
);
const CoachCol = ({ title, items, color }) => (
  <div>
    <p className={`font-semibold mb-1 ${color}`}>{title}</p>
    <ul className="space-y-1">
      {(items || []).map((it, i) => <li key={i} className="text-slate-700 flex gap-1.5"><span className="text-slate-400">•</span>{it}</li>)}
      {!(items || []).length && <li className="text-slate-400">–</li>}
    </ul>
  </div>
);

// --------------------------------------------------------------------------- explain chat
const ExplainChat = ({ submissionId }) => {
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [sid, setSid] = useState(null);
  const suggestions = ['Why did I lose marks?', 'What would a full-mark answer include?', 'Rewrite one paragraph as a Grade 9 response', 'How can I improve my introduction?'];

  const ask = async (question) => {
    if (!question.trim()) return;
    setMessages((m) => [...m, { role: 'student', content: question }]);
    setQ('');
    setLoading(true);
    try {
      const r = await axios.post(`${API}/mark-my-work/explain`,
        { submission_id: submissionId, question, session_id: sid },
        { headers: authHeader() });
      setSid(r.data.session_id);
      setMessages((m) => [...m, { role: 'examinor', content: r.data.answer }]);
    } catch {
      setMessages((m) => [...m, { role: 'examinor', content: 'Sorry, I could not answer that right now.' }]);
    } finally { setLoading(false); }
  };

  return (
    <Card className="border-indigo-200 shadow-sm" data-testid="mmw-explain-chat">
      <CardContent className="p-4">
        <div className="space-y-3 max-h-80 overflow-y-auto mb-3">
          {messages.length === 0 && (
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button key={s} onClick={() => ask(s)} className="text-xs px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100" data-testid="mmw-suggestion">{s}</button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`text-sm rounded-lg px-3 py-2 ${m.role === 'student' ? 'bg-slate-100 text-slate-800 ml-8' : 'bg-indigo-50 text-slate-800 mr-8'}`}>
              <span className="whitespace-pre-wrap">{m.content}</span>
            </div>
          ))}
          {loading && <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Examinor is thinking…</div>}
        </div>
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && ask(q)}
            placeholder="Ask about your marks…" data-testid="mmw-explain-input" />
          <Button onClick={() => ask(q)} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="mmw-explain-send"><Send className="h-4 w-4" /></Button>
        </div>
      </CardContent>
    </Card>
  );
};
